import { useState, useEffect, useContext, useMemo } from "react";
import { Typography, useMediaQuery, useTheme } from "@mui/material";
import PageContext from "../../components/Common/PageContext";
import { LoadingDialog } from "../../components/Loading/LoadingDialog";
import { ModalWithActionButton } from "../../components/Modal/ModalWithActionButton";
import { useEdiTrackingOutstandingInvoices } from "./api/useEdiTrackingOutstandingInvoices";
import { useHistory } from "react-router-dom";
import { translateError } from "../../util/axiosUtils";
import { EdiTrackingOutstandingInvoiceItem } from "@/common/EdiTrackingOutstandingInvoiceItem";
import OutstandingEdiInvoiceCardList from "../../components/Invoice/OustandingEdiInvoiceCardList";
import TabGroup from "../../components/Tab/TabGroup";
import { useStyles } from "../../components/ReceiveList/Receive.styles";

const OutstandingEdiByDate = () => {
	const history = useHistory();
	const theme = useTheme();
	const classes = useStyles();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const [outstandingEdiSummaryList, setOutstandingEdiSummaryList] = useState<EdiTrackingOutstandingInvoiceItem[]>([]);
	const [activeTab, setActiveTab] = useState<number>(0);
	const [pageContext] = useContext(PageContext);
	const { userLocation } = pageContext;
	const tab1MinDays = 5;
	const tab1MaxDays = 7;
	const tab2MinDays = 8;
	const tab2MaxDays = 14;
	const tab3MinDays = 15;
	const [{ data: listEdiTrackingOutstandingInvoicesResponse, loading: isLoading, error: apiError }] = useEdiTrackingOutstandingInvoices(userLocation);

	useEffect(() => {
		const {invoices} = listEdiTrackingOutstandingInvoicesResponse ?? {};
		if (!invoices || invoices.length === 0) {
			setOutstandingEdiSummaryList([]);
			return;
		}
		invoices.sort((a, b) => {
			return b.daysOverdue - a.daysOverdue;
		});
		setOutstandingEdiSummaryList(invoices);
	}, [listEdiTrackingOutstandingInvoicesResponse, userLocation]);

	const onSelectInvoiceAsync = async (invoiceNumber) => {
		try {
			const invoice = outstandingEdiSummaryList.find((i) => i.invoiceNumber === invoiceNumber);
			if (!invoice) {
				throw new Error("Invoice not found");
			}
			 history.push(
					`/outstandingEdiInvoice?invoiceNumber=${invoice.invoiceNumber}&purchaseOrderNumber=${invoice.purchaseOrderNumber}`
				); 
			} catch (e) {
				console.log(e);
				history.push("/");
			}
		};

	// Filter invoices by days outstanding categories
	const filteredInvoices = useMemo(() => {
		if (activeTab === 0) {
			return outstandingEdiSummaryList; // All invoices
		} else if (activeTab === 1) {
			return outstandingEdiSummaryList.filter(inv => inv.daysOverdue >= tab1MinDays && inv.daysOverdue <= tab1MaxDays); // Green Tab
		} else if (activeTab === 2) {
			return outstandingEdiSummaryList.filter(inv => inv.daysOverdue >= tab2MinDays && inv.daysOverdue <= tab2MaxDays); // Yellow Tab
		} else if (activeTab === 3) {
			return outstandingEdiSummaryList.filter(inv => inv.daysOverdue >= tab3MinDays); // Red Tab
		}
		return outstandingEdiSummaryList;
	}, [outstandingEdiSummaryList, activeTab]);

	// Calculate counts for each category
	const categoryCounts = useMemo(() => {
		return {
			all: outstandingEdiSummaryList.length,
			green: outstandingEdiSummaryList.filter(inv => inv.daysOverdue >= tab1MinDays && inv.daysOverdue <= tab1MaxDays).length,
			yellow: outstandingEdiSummaryList.filter(inv => inv.daysOverdue >= tab2MinDays && inv.daysOverdue <= tab2MaxDays).length,
			red: outstandingEdiSummaryList.filter(inv => inv.daysOverdue >= tab3MinDays).length,
		};
	}, [outstandingEdiSummaryList]);

	const tabPanels = [
		{
			label: `All (${categoryCounts.all})`,
			child: <OutstandingEdiInvoiceCardList
				invoices={filteredInvoices}
				greenMinDays={tab1MinDays}
				yellowMinDays={tab2MinDays}
				redMinDays={tab3MinDays}
				onView={onSelectInvoiceAsync} />
		},
		{
			label: isMobile ? `🟢 ${categoryCounts.green}` : `🟢 5-7 Days (${categoryCounts.green})`,
			child: <OutstandingEdiInvoiceCardList
				invoices={filteredInvoices}
				greenMinDays={tab1MinDays}
				yellowMinDays={tab2MinDays}
				redMinDays={tab3MinDays}
				onView={onSelectInvoiceAsync} />
		},
		{
			label: isMobile ? `🟡 ${categoryCounts.yellow}` : `🟡 8-14 Days (${categoryCounts.yellow})`,
			child: <OutstandingEdiInvoiceCardList
				invoices={filteredInvoices}
				greenMinDays={tab1MinDays}
				yellowMinDays={tab2MinDays}
				redMinDays={tab3MinDays}
				onView={onSelectInvoiceAsync} />
		},
		{
			label: isMobile ? `🔴 ${categoryCounts.red}` : `🔴 15+ Days (${categoryCounts.red})`,
			child: <OutstandingEdiInvoiceCardList
				invoices={filteredInvoices}
				greenMinDays={tab1MinDays}
				yellowMinDays={tab2MinDays}
				redMinDays={tab3MinDays}
				onView={onSelectInvoiceAsync} />
		}
	];

	const handleTabChange = (tabIndex: number) => {
		setActiveTab(tabIndex);
	};

	return (
		<div style={{ padding: isMobile ? '4px' : '8px' }}>
			<Typography className={classes.statusTitle}>Outstanding EDI Invoices</Typography>
			{isLoading && <LoadingDialog message={"Retrieving Invoices..."} isOpen={isLoading} />}
			{apiError && (
				<ModalWithActionButton
					title="Error"
					alertType="error"
					primaryButtonName="Ok"
					onPrimaryButtonClick={() => history.push("/")}
					isOpen={true}
					message={translateError(apiError)}
				/>
			)}
			{!isLoading && !apiError && (
				<TabGroup
					tabPanels={tabPanels}
					handleTabChange={handleTabChange}
					activeTab={activeTab}
				/>
				)}
		</div>
	);
};

export default OutstandingEdiByDate;
